import React, { BaseSyntheticEvent, FC, useEffect, useState } from "react";
import { CloseButton, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { moveItem } from "../../../graphql/state";
import { useReactiveVar } from "@apollo/client";
import { Field, Formik } from "formik";
import { Form, useNavigate, useParams } from "react-router-dom";
import { useUserQuery } from "../../../graphql/menu.hooks";
import {
  useMassMoveFolderAndPageMutation,
  usePageQuery,
} from "../../../graphql/page.hooks";
import useGetUrlLocale from "../../../hooks/useGetUrlLocale";
import { Error } from "../../../utils";
import { CustomToast } from "../../inputs/toast";
import {
  useFolderDescendantsQuery,
  useFolderShowQuery,
} from "../../../graphql/folder.hooks";
import { movePageSchema } from "./validation/moveSchema";
import ReactSelect from "../../inputs/select";
import { SelectOption } from "../../../types";
import { useGetOptions } from "../../../hooks/useGetOptions";

type Props = {
  show: boolean;
  onCancel: () => void;
};

export const MoveModal: FC<Props> = ({ show, onCancel }) => {
  const { t } = useTranslation();
  const lng = useGetUrlLocale();
  const { projectId: projectID, id: folderID } = useParams();
  const navigate = useNavigate();
  const moveItemState = useReactiveVar(moveItem);

  const {
    data: user,
    loading: userLoading,
    refetch: userRefetch,
  } = useUserQuery();
  const [massMove, { loading: moveLoading }] =
    useMassMoveFolderAndPageMutation();

  const [projectId, setProjectId] = useState<number | null>(0);
  const [toFolderId, setToFolderId] = useState<number | null>(0);
  const [folders, setFolders] = useState(null);
  const [isShowToast, setIsShowToast] = useState<boolean>(false);
  const { domainOptions, folderOptions, accessLoading } = useGetOptions({
    projectId: +projectId,
    setFolders,
  });

  let isDisabledByLocation = false;

  if (folderID) {
    isDisabledByLocation = +projectID === projectId && toFolderId === +folderID;
  } else {
    isDisabledByLocation = toFolderId ? false : +projectID === projectId;
  }

  const isMoveFolder =
    moveItemState.data.folderIds.length === 1 &&
    moveItemState.data.pageIds.length === 0;
  const isMovePage =
    moveItemState.data.pageIds.length === 1 &&
    moveItemState.data.folderIds.length === 0;
  const isMoveFiles =
    [...moveItemState.data.folderIds, ...moveItemState.data.pageIds].length > 1;

  const { data: folderData, refetch: refetchDescendant } =
    useFolderDescendantsQuery({
      variables: { folderIds: moveItemState.data.folderIds },
    });

  const { data: folder } = useFolderShowQuery({
    variables: { folderId: moveItemState.data.folderIds[0] },
    skip: !isMoveFolder,
  });

  const { data: page } = usePageQuery({
    variables: { id: moveItemState.data.pageIds[0] },
    skip: !isMovePage,
  });

  useEffect(() => {
    userRefetch();
    refetchDescendant();
  }, [folderData, user, refetchDescendant, userRefetch]);

  const closeModal = () => {
    setProjectId(0);
    setFolders(null);
    onCancel();
  };

  const closeByBackground = (e: BaseSyntheticEvent) => {
    e.stopPropagation();
    if (e.target.id === "confirmModal") closeModal();
  };

  interface IinitialValues {
    domainId: number | null;
    folderId: number | string;
  }

  const initialValues: IinitialValues = {
    domainId: projectId || null,
    folderId: folders || "",
  };
  const tryMovePage = async (values: any) => {
    const moveData = {
      toFolderId: toFolderId === 0 || toFolderId === null ? null : toFolderId,
      domainId: +values.domainId,
      ...moveItemState.data,
    };

    try {
      const { data } = await massMove({
        variables: moveData,
      });
      if (data?.massMoveFolderAndPage) {
        if (toFolderId === null || toFolderId === 0 || isNaN(toFolderId)) {
          navigate(`${lng}/projects/${values.domainId}`);
        } else {
          navigate(`${lng}/projects/${values.domainId}/folder/${toFolderId}`);
        }

        setProjectId(0);
        setFolders(null);
        setToFolderId(null);
        moveItem({
          isActive: false,
          data: { pageIds: [], folderIds: [] },
        });
      }
    } catch (error) {
      if (error.message === Error.UNIQUE_URL) {
        setIsShowToast(true);
      }
      console.error(error);
    }
  };

  return (
    <div
      id="confirmModal"
      className={`modal ${show ? "show d-flex" : ""} align-items-center justify-content-center`}
      style={{
        display: show ? "block" : "none",
        background: "rgb(0, 0, 0, 0.6)",
      }}
      onClick={(e) => closeByBackground(e)}
    >
      <div className="modal-dialog">
        <div className="modal-content text-break">
          <div className="modal-header border-0 d-flex justify-content-between align-items-baseline">
            <div className="d-flex flex-column">
              <h5 className="modal-title fw-700 fs-20">
                {isMoveFolder && t("projects.moveFolder")}
                {isMovePage && t("projects.movePage")}
                {isMoveFiles && t("modal.moveFiles")}
              </h5>
              {isMoveFiles && (
                <p className="text-gray m-0 text-lowercase">
                  <span className="fw-bold text-dark">
                    {moveItemState.data.folderIds.length}
                  </span>{" "}
                  {moveItemState.data.folderIds.length > 1
                    ? t("share.folders")
                    : t("menu.folder")}{" "}
                  /{" "}
                  <span className="fw-bold text-dark">
                    {moveItemState.data.pageIds.length}
                  </span>{" "}
                  {moveItemState.data.pageIds.length > 1
                    ? t("share.pages")
                    : t("menu.page")}
                </p>
              )}
            </div>
            <CloseButton
              onClick={closeModal}
              style={{ height: "16px", width: "16px", opacity: "1" }}
            />
          </div>
          <div className="modal-body border-0 d-flex flex-column justify-content-start p-3 pt-0">
            {isMoveFiles && (
              <p className="text-gray mb-3">{t("modal.moveText")}</p>
            )}
            {isMoveFolder && (
              <p className="text-gray mb-3">
                {t("modal.moveFolderFirst")}
                <span className="fw-bold text-dark">
                  {" "}
                  {folder?.folderShow.folder.title}{" "}
                </span>
                <p>{t("modal.moveFolderLast")}</p>
              </p>
            )}
            {isMovePage && (
              <p className="text-gray mb-3">
                {t("modal.movePageFirst")}
                <span className="fw-bold text-dark"> {page?.page.title} </span>
                <p>{t("modal.movePageLast")}</p>
              </p>
            )}
            <p className="mb-2 fs-6 fw-bold text-dark">
              {t("modal.chooseProject")}
            </p>
            <Formik
              initialValues={initialValues}
              validationSchema={movePageSchema(t)}
              onSubmit={tryMovePage}
              enableReinitialize
            >
              {({ setFieldValue, handleSubmit, values }) => (
                <Form className="g-4 d-flex justify-content-center align-items-center bg-white rounded">
                  <div className="col-12">
                    <Field name="domainId">
                      {() => (
                        <ReactSelect
                          className="mb-3"
                          options={domainOptions}
                          value={domainOptions.find(
                            (domain) => domain.value === +values.domainId
                          )}
                          onChange={(option: SelectOption) => {
                            setFieldValue("domainId", option.value);
                            setProjectId(option.value);
                          }}
                          placeholder="Select Project"
                          isLoading={userLoading || accessLoading}
                          label={t("page.project")}
                        />
                      )}
                    </Field>
                    <Field name="folderId">
                      {() => (
                        <ReactSelect
                          className="mb-3 position-relative"
                          options={folderOptions}
                          value={folderOptions?.find(
                            (domain) => domain.value === +values.folderId
                          )}
                          onChange={(option: SelectOption) => {
                            setFieldValue("folderId", option.value);
                            setToFolderId(option.value);
                          }}
                          placeholder="Select Folder"
                          isLoading={userLoading}
                          isDisabled={!folders?.length}
                          label={t("page.folder")}
                        />
                      )}
                    </Field>
                    <div className="shareSettings d-flex align-items-center justify-content-between">
                      <button
                        type="button"
                        className="buttomCol btn btn-outline-dark fw-normal dark-hover col-md-4 me-3 me-md-0"
                        onClick={closeModal}
                      >
                        {t("profile.cancel")}
                      </button>
                      <button
                        id="customizeButton"
                        type="button"
                        disabled={
                          projectId === 0 ||
                          !projectId ||
                          moveLoading ||
                          isDisabledByLocation
                        }
                        onClick={() => handleSubmit()}
                        className={`buttomCol btn btn-primary col-md-4 text-white ${(projectId === 0 || !projectId || isDisabledByLocation) && "disable"}`}
                      >
                        {t("folder.move")}
                        {moveLoading && (
                          <Spinner
                            animation="border"
                            role="status"
                            className="ms-2"
                          >
                            <span className="visually-hidden">
                              {t("profile.loading")}...
                            </span>
                          </Spinner>
                        )}
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
            <CustomToast
              isShow={isShowToast}
              setShow={setIsShowToast}
              background="bg-danger"
              message={`${t("page.uniqueUrl")}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
